/**
 * src/lib/voice/useUploadJobs.js — TICKET-UPLOAD-PROGRESS-UX-T1 (CEO 2026-06-02).
 *
 * React hook backing the direct-upload jobs in VoiceQueueTray. Two jobs:
 *   1. Expose the uploadStatusStore via useSyncExternalStore (stable snapshot).
 *   2. Drive the POST-UPLOAD phases for direct jobs. The upload phase
 *      (uploading + pct) is owned by VoiceFileUploadBtn; once a job flips to
 *      'uploaded' with an `uploadId`, the button is done and may unmount — the
 *      tray (always mounted at app root) takes over polling voice.upload_progress
 *      and advances the job processing → classifying → ready.
 *
 * Direct uploads bypass IndexedDB (iOS large-blob limit), so useVoiceQueueState
 * (which polls IDB) never sees them — this hook is their poll loop, patching the
 * module-singleton store instead of an IDB row.
 *
 * fetchProgress is injectable for tests; defaults to a lazy import of the
 * voice-v2 helper so the hook stays cheap to mount.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { getUploadJobs, subscribeUploadJobs, patchUploadJob, uploadStatusToPhase } from './uploadStatusStore';
import { logAppError } from '../errorReporter';

// Phases that mean "registered with the server, still in flight" → poll the
// server for the real terminal. 'uploading' is pre-register (no uploadId yet).
const IN_FLIGHT_PHASES = new Set(['uploaded', 'processing', 'classifying']);
const SERVER_POLL_THROTTLE_MS = 3000;
const STUCK_AFTER_MS = 10 * 60 * 1000;

// Server status (voice.upload_progress) → store job phase. TICKET-1127: delegates
// to the ONE canonical map (uploadStatusToPhase) — a private copy here drifted from
// it and froze HistoryTab's stepper. Jobs polled here already hold an uploadId, so
// a pre-register 'uploading'/'queued' status still reads as 'uploaded'.
export function mapServerStatusToPhase(serverStatus) {
  const phase = uploadStatusToPhase(serverStatus);
  return phase === 'uploading' ? 'uploaded' : phase;
}

export function useUploadJobs({ pollMs = 3000, fetchProgress } = {}) {
  const jobs = useSyncExternalStore(subscribeUploadJobs, getUploadJobs);
  const fetchProgressRef = useRef(fetchProgress);
  fetchProgressRef.current = fetchProgress;
  const lastPollRef = useRef(new Map()); // job.id → last server poll ts
  const stuckReportedRef = useRef(new Set()); // job ids already reported stuck

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    async function getProgressLazy(uploadId) {
      if (typeof fetchProgressRef.current === 'function') return fetchProgressRef.current(uploadId);
      const mod = await import('../api/voice-v2.js');
      return mod.fetchUploadProgress(uploadId);
    }

    async function pollOne(job, now) {
      const last = lastPollRef.current.get(job.id) || 0;
      if (now - last < SERVER_POLL_THROTTLE_MS) return;
      lastPollRef.current.set(job.id, now);
      try {
        const p = await getProgressLazy(job.uploadId);
        if (cancelled || !p) return;
        const phase = mapServerStatusToPhase(p.status);
        const serverErr = p.error_message ?? p.error ?? null;
        const patch = {};
        if (phase && phase !== job.phase) patch.phase = phase;
        if (typeof p.chunks_done === 'number' && p.chunks_done !== job.chunksDone) patch.chunksDone = p.chunks_done;
        if (typeof p.chunks_total === 'number' && p.chunks_total !== job.chunksTotal) patch.chunksTotal = p.chunks_total;
        if (p.headline && p.headline !== job.headline) patch.headline = p.headline;
        if (serverErr && serverErr !== job.error) patch.error = serverErr;
        if (Object.keys(patch).length) patchUploadJob(job.id, patch);
        // TICKET-1127: a job still in flight 10 min after it started is the
        // "stuck on Updating project" symptom. Emit ONCE per job with what the
        // server actually said, so the next occurrence is diagnosable from the
        // spine instead of guessed at.
        const ageMs = job.createdAt ? now - job.createdAt : 0;
        if (ageMs > STUCK_AFTER_MS && !(phase === 'ready' || phase === 'error') && !stuckReportedRef.current.has(job.id)) {
          stuckReportedRef.current.add(job.id);
          logAppError({
            type: 'upload_stuck',
            message: `upload job in flight ${Math.round(ageMs / 60000)}min`,
            view: 'useUploadJobs',
            metadata: { step: 'voice.upload_stuck', upload_id: job.uploadId, job_phase: job.phase, server_status: p.status ?? null, mapped_phase: phase },
          });
        }
      } catch {
        // spine-emit-ok: progress poll is best-effort; fetchUploadProgress logs its own HTTP errors
      }
    }

    async function poll() {
      const now = Date.now();
      const inFlight = getUploadJobs().filter((j) => j.uploadId && IN_FLIGHT_PHASES.has(j.phase));
      await Promise.all(inFlight.map((j) => pollOne(j, now)));
    }

    poll();
    timer = setInterval(poll, pollMs);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [pollMs]);

  return jobs;
}

// Test seam — pure mapper, no React.
export const _internal = { mapServerStatusToPhase, IN_FLIGHT_PHASES };
